import { Grid2, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createRentalDay } from '@openapi/timeslots/timeslots';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CommonButton from '../../components/atom/CommonButton';
import useNotify from '../../hooks/useNotify';
import { getDateKeyInTimezone, parseDateTimeLocal } from '../../helpers/formatDateTime';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import { resolveSlotLabel } from '../../helpers/slotTemplate';

function CreateRentalDayPage() {
    const { t } = useTranslation();
    const { showId } = useParams();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { template } = useSlotTemplate(showId);

    // Default to today's date in the venue timezone, matching the bulk page.
    const defaultDateStr = getDateKeyInTimezone(new Date());

    const validateSchema = Yup.object().shape({
        date: Yup.string().required(t('common.required')),
        slots: Yup.array().of(
            Yup.object().shape({
                startTime: Yup.string().required(t('common.required')),
            }),
        ),
    });

    // Each rental-day slot starts from its template default time-of-day.
    const initialSlots = template.map((s) => ({
        slotKey: s.key,
        durationMinutes: s.durationMinutes ?? 60,
        startTime: s.timeOfDay || '09:00',
    }));

    const onSubmit = async (values) => {
        try {
            const toIso = (dateStr, timeStr) => parseDateTimeLocal(`${dateStr}T${timeStr}`);

            // The backend creates every slot atomically (single transaction) and
            // anchors the group, so there are no orphaned timeslots on failure.
            await createRentalDay(parseInt(showId, 10), {
                slots: values.slots.map((s) => ({
                    slotKey: s.slotKey,
                    startTime: toIso(values.date, s.startTime),
                    durationMinutes: s.durationMinutes,
                })),
            });

            notifySuccess(t('timeslots.notifications.rentalDayCreated'));
            navigate(`/shows/${showId}/timeslots`);
        } catch (error) {
            notifyError(error);
        }
    };

    return (
        <Formik
            enableReinitialize
            onSubmit={onSubmit}
            initialValues={{
                date: defaultDateStr,
                slots: initialSlots,
            }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={t('timeslots.createRentalDay')}
                        headline={
                            <Grid2 display="flex" flexDirection="row" gap={2}>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        onClick={() => navigate(`/shows/${showId}/timeslots`)}
                                        label={t('common.cancel')}
                                        buttonType="secondary"
                                    />
                                </Grid2>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        disabled={isSubmitting || values.slots.length === 0}
                                        type="submit"
                                        label={
                                            isSubmitting ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : (
                                                t('common.save')
                                            )
                                        }
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </Grid2>
                        }
                    >
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle title={t('timeslots.createRentalDay')}>
                                <Grid2 size={{ xs: 12 }}>
                                    <Alert severity="info">
                                        {t('timeslots.createRentalDayHint')}
                                    </Alert>
                                </Grid2>
                                {values.slots.length === 0 && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <Alert severity="warning">
                                            {t('shows.columns.slotTemplateRentalOffHint')}
                                        </Alert>
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label={t('timeslots.rentalDate')}
                                        value={values.date}
                                        onChange={(ev) => setFieldValue('date', ev.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={touched.date && Boolean(errors.date)}
                                        helperText={touched.date ? errors.date : ''}
                                    />
                                </Grid2>
                                {values.slots.map((slot, index) => {
                                    const slotErr = errors.slots?.[index];
                                    const slotTouched = touched.slots?.[index];
                                    return (
                                        <Grid2 size={{ xs: 12 }} key={slot.slotKey}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {resolveSlotLabel(template, slot)}
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                type="time"
                                                label={t('timeslots.columns.startTime')}
                                                value={slot.startTime}
                                                onChange={(ev) =>
                                                    setFieldValue(
                                                        `slots.${index}.startTime`,
                                                        ev.target.value,
                                                    )
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                error={
                                                    slotTouched?.startTime &&
                                                    Boolean(slotErr?.startTime)
                                                }
                                                helperText={
                                                    slotTouched?.startTime ? slotErr?.startTime : ''
                                                }
                                            />
                                        </Grid2>
                                    );
                                })}
                            </BlockWithTitle>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default CreateRentalDayPage;
